/**
 * Demo Interceptor
 *
 * Active only when environment.demo is true (see app.config.ts). Short-circuits
 * outgoing HTTP requests that match a known backend endpoint and returns a
 * fabricated response built from in-memory demo data (DemoStore), so the whole
 * app runs without a real backend. Requests that don't match a known pattern
 * fall through to `next(req)` untouched, so gaps in coverage surface loudly
 * (a failed network call) instead of being silently swallowed.
 */

import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { ApiConfiguration } from '../../api/api-configuration';
import { DemoStore } from './demo-store';
import { buildDemoAccessToken, buildDemoRefreshToken } from './demo-jwt';
import { buildPageResponse, readPageParams } from './demo-pagination.util';
import { buildSnapshot, buildTimeSeries, buildBreakdown } from './fixtures/analytics.fixtures';
import { AnalyticsMetric, AnalyticsPeriod, BreakdownDimension } from '../../services/analytics.service';
import { AiInsightType } from '../../services/ai-insight.service';
import { buildHealthSummary, DEMO_INCIDENT_STATUS } from './fixtures/app-health.fixtures';
import { buildReviewSummary } from './fixtures/reviews.fixtures';
import { DEMO_INDUSTRIES } from './fixtures/industries.fixtures';

interface DemoResult {
  status: number;
  body?: unknown;
}

type DemoHandler = (ctx: { path: string; query: URLSearchParams; body: any; store: DemoStore }) => DemoResult;

interface DemoRoute {
  method: string;
  pattern: RegExp;
  handle: DemoHandler;
}

function ok(body?: unknown): DemoResult {
  return { status: 200, body };
}

function tokenResponse(): DemoResult {
  return ok({ accessToken: buildDemoAccessToken(), refreshToken: buildDemoRefreshToken() });
}

function notFound(message = 'Not found'): DemoResult {
  return { status: 404, body: { message } };
}

const routes: DemoRoute[] = [
  // ---------------------------------------------------------------- auth
  { method: 'POST', pattern: /^\/api\/v1\/auth\/login$/, handle: () => tokenResponse() },
  { method: 'POST', pattern: /^\/api\/v1\/auth\/magic-link$/, handle: () => ok({ message: 'If an account exists for this email, a sign-in link has been sent.' }) },
  { method: 'POST', pattern: /^\/api\/v1\/auth\/magic-link\/verify$/, handle: () => tokenResponse() },
  { method: 'POST', pattern: /^\/api\/v1\/auth\/google$/, handle: () => tokenResponse() },
  { method: 'POST', pattern: /^\/api\/v1\/auth\/linkedin$/, handle: () => tokenResponse() },
  { method: 'POST', pattern: /^\/api\/v1\/auth\/refresh$/, handle: () => tokenResponse() },
  {
    method: 'PATCH',
    pattern: /^\/api\/v1\/auth\/data-sharing-preference$/,
    handle: ({ body, store }) => {
      const optIn = !!body?.optIn;
      store.updateDataSharingOptIn(optIn);
      return ok({ dataSharingOptIn: optIn });
    }
  },
  {
    method: 'POST',
    pattern: /^\/api\/v1\/auth\/complete-profile$/,
    handle: ({ store }) => ok(store.adminUser)
  },
  {
    method: 'PATCH',
    pattern: /^\/api\/v1\/auth\/referral-source$/,
    handle: ({ body, store }) => {
      store.adminUser.referralSource = body?.referralSource;
      store.adminUser.referredByEmail = body?.referredByEmail;
      return ok(store.adminUser);
    }
  },
  { method: 'POST', pattern: /^\/api\/v1\/auth\/resend-verification-email$/, handle: () => ok({ message: 'Verification email sent.' }) },
  { method: 'GET', pattern: /^\/api\/v1\/auth\/verify-email$/, handle: () => ok({ message: 'Email verified.' }) },
  { method: 'POST', pattern: /^\/api\/v1\/auth\/forgot-password$/, handle: () => ok({ message: 'If an account with that email exists, a password reset link has been sent.' }) },
  { method: 'POST', pattern: /^\/api\/v1\/auth\/reset-password$/, handle: () => ok({ message: 'Your password has been reset.' }) },
  { method: 'GET', pattern: /^\/api\/v1\/auth\/profile$/, handle: ({ store }) => ok(store.adminUser) },
  {
    method: 'POST',
    pattern: /^\/api\/v1\/customer-profiles\/register$/,
    handle: ({ store }) =>
      ok({
        message: 'Registration successful. Please check your email to verify your account.',
        adminUser: store.adminUser,
        customerProfile: store.customerProfile,
        subscription: store.adminUser.subscription,
        tokens: { accessToken: buildDemoAccessToken(), refreshToken: buildDemoRefreshToken() }
      })
  },
  { method: 'POST', pattern: /^\/api\/v1\/invitations\/accept$/, handle: () => ok({ message: 'Your account is ready. You can now sign in.' }) },

  // ---------------------------------------------------------------- apps
  {
    method: 'GET',
    pattern: /^\/api\/v1\/apps\/my-apps$/,
    handle: ({ query, store }) => {
      const { page, size } = readPageParams(query);
      return ok(buildPageResponse(store.apps, page, size));
    }
  },
  {
    method: 'GET',
    pattern: /^\/api\/v1\/apps$/,
    handle: ({ query, store }) => {
      const { page, size } = readPageParams(query);
      return ok(buildPageResponse(store.apps, page, size));
    }
  },
  {
    method: 'GET',
    pattern: /^\/api\/v1\/apps\/(\d+)$/,
    handle: ({ path, store }) => {
      const id = Number(path.match(/^\/api\/v1\/apps\/(\d+)$/)?.[1]);
      const app = store.apps.find((a) => a.id === id);
      return app ? ok(app) : notFound('App not found');
    }
  },
  {
    method: 'POST',
    pattern: /^\/api\/v1\/apps$/,
    handle: ({ body, store }) => ok(store.addApp(body))
  },
  {
    method: 'PUT',
    pattern: /^\/api\/v1\/apps\/(\d+)$/,
    handle: ({ path, body, store }) => {
      const id = Number(path.match(/^\/api\/v1\/apps\/(\d+)$/)?.[1]);
      const app = store.updateApp(id, body);
      return app ? ok(app) : notFound('App not found');
    }
  },
  {
    method: 'DELETE',
    pattern: /^\/api\/v1\/apps\/(\d+)$/,
    handle: ({ path, store }) => {
      const id = Number(path.match(/^\/api\/v1\/apps\/(\d+)$/)?.[1]);
      store.deleteApp(id);
      return { status: 200 };
    }
  },
  {
    method: 'PATCH',
    pattern: /^\/api\/v1\/apps\/(\d+)\/activate$/,
    handle: ({ path, store }) => {
      const id = Number(path.match(/^\/api\/v1\/apps\/(\d+)\/activate$/)?.[1]);
      const app = store.activateApp(id);
      return app ? ok(app) : notFound('App not found');
    }
  },
  {
    method: 'POST',
    pattern: /^\/api\/v1\/integrations\/google-play\/sync$/,
    handle: ({ query, store }) => {
      const appId = Number(query.get('appId'));
      const app = store.syncGooglePlay(appId);
      return app ? ok({ status: app.googlePlayConnectionStatus }) : notFound('App not found');
    }
  },
  {
    method: 'POST',
    pattern: /^\/api\/v1\/integrations\/firebase\/sync$/,
    handle: ({ query, store }) => {
      const appId = Number(query.get('appId'));
      const app = store.syncFirebase(appId);
      return app ? ok({ status: app.firebaseConnectionStatus }) : notFound('App not found');
    }
  },
  { method: 'POST', pattern: /^\/api\/v1\/integrations\/google-play\/test$/, handle: () => ok({ status: 'READY' }) },
  { method: 'POST', pattern: /^\/api\/v1\/integrations\/firebase\/test$/, handle: () => ok({ status: 'READY' }) },

  // ---------------------------------------------------------------- org / team management
  {
    method: 'GET',
    pattern: /^\/api\/v1\/customer-profiles\/(\d+)$/,
    handle: ({ store }) => ok(store.customerProfile)
  },
  {
    method: 'GET',
    pattern: /^\/api\/v1\/customer-profiles\/(\d+)\/users$/,
    handle: ({ query, store }) => {
      const { page, size } = readPageParams(query);
      return ok(buildPageResponse(store.teamMembers, page, size));
    }
  },
  {
    method: 'DELETE',
    pattern: /^\/api\/v1\/customer-profiles\/(\d+)\/users\/(\d+)$/,
    handle: ({ path, store }) => {
      const userId = Number(path.match(/\/users\/(\d+)$/)?.[1]);
      store.removeTeamMember(userId);
      return { status: 200 };
    }
  },
  {
    method: 'GET',
    pattern: /^\/api\/v1\/customer-profiles\/(\d+)\/can-add-users$/,
    handle: ({ store }) => ok({ canAddUsers: store.canAddUsers() })
  },
  {
    method: 'GET',
    pattern: /^\/api\/v1\/customer-profiles\/(\d+)\/invitations$/,
    handle: ({ store }) => ok(store.invitations)
  },
  {
    method: 'POST',
    pattern: /^\/api\/v1\/customer-profiles\/(\d+)\/invitations$/,
    handle: ({ body, store }) => ok(store.addInvitation(body?.email, body?.role))
  },
  {
    method: 'DELETE',
    pattern: /^\/api\/v1\/customer-profiles\/(\d+)\/invitations\/(\d+)$/,
    handle: ({ path, store }) => {
      const invitationId = Number(path.match(/\/invitations\/(\d+)$/)?.[1]);
      store.invitations = store.invitations.filter((i) => i.id !== invitationId);
      return { status: 200 };
    }
  },

  // ---------------------------------------------------------------- analytics
  {
    method: 'GET',
    pattern: /^\/api\/v1\/analytics\/app-metrics$/,
    handle: ({ query }) => {
      const appId = query.get('appId') ?? 'all';
      const period = (query.get('period') as AnalyticsPeriod) ?? 'THIS_MONTH';
      return ok(buildSnapshot(appId, period));
    }
  },
  {
    method: 'GET',
    pattern: /^\/api\/v1\/analytics\/app-metrics\/timeseries$/,
    handle: ({ query }) => {
      const appId = query.get('appId') ?? 'all';
      const metric = (query.get('metric') as AnalyticsMetric) ?? 'ACTIVE_USERS';
      return ok(buildTimeSeries(appId, metric));
    }
  },
  {
    method: 'GET',
    pattern: /^\/api\/v1\/analytics\/app-metrics\/breakdown$/,
    handle: ({ query }) => {
      const appId = query.get('appId') ?? 'all';
      const dimension = (query.get('dimension') as BreakdownDimension) ?? 'COUNTRY';
      return ok(buildBreakdown(appId, dimension));
    }
  },

  // ---------------------------------------------------------------- AI insights
  {
    method: 'GET',
    pattern: /^\/api\/v1\/ai-insights$/,
    handle: ({ query, store }) => {
      const appId = Number(query.get('appId'));
      const type = query.get('type') as AiInsightType | null;
      const insights = store.aiInsights.filter(
        (i) => i.appId === appId && (!type || i.insightType === type)
      );
      return ok(insights);
    }
  },
  {
    method: 'POST',
    pattern: /^\/api\/v1\/ai-insights\/generate$/,
    handle: ({ body, store }) => ok(store.generateInsight(body?.appId, body?.type))
  },
  {
    method: 'GET',
    pattern: /^\/api\/v1\/ai-insights\/(\d+)$/,
    handle: ({ path, store }) => {
      const id = Number(path.match(/\/ai-insights\/(\d+)$/)?.[1]);
      const insight = store.aiInsights.find((i) => i.id === id);
      return insight ? ok(insight) : notFound('Insight not found');
    }
  },

  // ---------------------------------------------------------------- app health
  {
    method: 'GET',
    pattern: /^\/api\/v1\/app-health\/summary$/,
    handle: ({ query }) => ok(buildHealthSummary(query.get('appId')))
  },
  { method: 'GET', pattern: /^\/api\/v1\/app-health\/incidents-by-status$/, handle: () => ok(DEMO_INCIDENT_STATUS) },

  // ---------------------------------------------------------------- reviews
  {
    method: 'GET',
    pattern: /^\/api\/v1\/reviews\/cursor$/,
    handle: ({ query, store }) => {
      const appId = query.get('appId');
      const searchTerm = query.get('searchTerm')?.toLowerCase();
      const minRating = query.get('minRating') ? Number(query.get('minRating')) : undefined;
      const maxRating = query.get('maxRating') ? Number(query.get('maxRating')) : undefined;
      const platform = query.get('platform');
      const sentiment = query.get('sentiment');
      const limit = query.get('limit') ? Number(query.get('limit')) : 20;
      const startDate = query.get('startDate');
      const endDate = query.get('endDate');
      const cursor = query.get('cursor');
      const offset = cursor ? Number(cursor) : 0;

      const filtered = store.reviews.filter((r) => {
        if (appId && String(r.appId) !== appId) return false;
        if (minRating != null && (r.rating ?? 0) < minRating) return false;
        if (maxRating != null && (r.rating ?? 0) > maxRating) return false;
        if (platform && r.platform !== platform) return false;
        if (sentiment && r.sentiment !== sentiment) return false;
        if (searchTerm && !r.content?.toLowerCase().includes(searchTerm)) return false;
        if (startDate && new Date(r.reviewDate ?? 0) < new Date(startDate)) return false;
        if (endDate && new Date(r.reviewDate ?? 0) > new Date(`${endDate.slice(0, 10)}T23:59:59.999Z`)) return false;
        return true;
      });

      const items = filtered.slice(offset, offset + limit);
      const nextOffset = offset + limit;
      const hasNext = nextOffset < filtered.length;
      return ok({ items, hasNext, nextCursor: hasNext ? String(nextOffset) : undefined });
    }
  },

  {
    method: 'GET',
    pattern: /^\/api\/v1\/reviews\/app\/(\d+)\/summary$/,
    handle: ({ path }) => {
      const appId = Number(path.match(/\/reviews\/app\/(\d+)\/summary$/)?.[1]);
      const summary = buildReviewSummary(appId);
      return summary ? ok(summary) : notFound('No reviews for this app yet');
    }
  },

  // ---------------------------------------------------------------- industries
  { method: 'GET', pattern: /^\/api\/v1\/industries\/list$/, handle: () => ok(DEMO_INDUSTRIES) }
];

function matchRoute(method: string, path: string): DemoRoute | undefined {
  return routes.find((r) => r.method === method && r.pattern.test(path));
}

export const demoInterceptor: HttpInterceptorFn = (req, next): Observable<any> => {
  const apiConfig = inject(ApiConfiguration);
  const store = inject(DemoStore);

  const rootUrl = apiConfig.rootUrl.replace(/\/+$/, '');
  if (!req.url.startsWith(rootUrl)) {
    return next(req);
  }

  // HttpRequest.url never includes query params (those live in req.params) — the full
  // URL with params serialized in is only available via urlWithParams.
  const [path, queryString] = req.urlWithParams.slice(rootUrl.length).split('?');
  const route = matchRoute(req.method, path);
  if (!route) {
    return next(req);
  }

  const result = route.handle({
    path,
    query: new URLSearchParams(queryString ?? ''),
    body: req.body,
    store
  });

  return of(result).pipe(
    delay(200 + Math.random() * 200),
    map((r) => new HttpResponse({ status: r.status, body: r.body ?? null }))
  );
};
